import React, { useState, useCallback, useMemo } from 'react';
import { useSchedule } from '../context/ScheduleContext';
import type { ResourcePanelConfig } from '../types';
import { MatchSummaryBanner } from '../matching/MatchSummaryBanner';
import { MatchedResourceCard } from '../matching/MatchedResourceCard';

const PANEL_MIN_WIDTH = 150;
const PANEL_MAX_WIDTH = 500;

// ─── Collapse strip ────────────────────────────────────────────────────────

function CollapsedStrip({ label, onExpand }: { label: string; onExpand: () => void }) {
  return (
    <div
      style={{
        width: 28,
        flexShrink: 0,
        borderRight: '1px solid #e2e8f0',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 10,
        userSelect: 'none',
      }}
    >
      <button
        onClick={onExpand}
        title={`Expand ${label} panel`}
        style={{
          background: 'none',
          border: '1px solid #e2e8f0',
          borderRadius: 4,
          cursor: 'pointer',
          padding: '3px 4px',
          fontSize: 11,
          color: '#64748b',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'rotate(180deg)',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
        }}
      >
        › {label}
      </button>
    </div>
  );
}

// ─── Search icon ───────────────────────────────────────────────────────────

function SearchIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// ─── Chevron icon (expand/collapse unmatched section) ─────────────────────

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="11" height="11" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ─── Main panel ────────────────────────────────────────────────────────────

export function ResourcePanel({ panelConfig }: { panelConfig: ResourcePanelConfig }) {
  const { config, resourcePage, setResourcePage, matchingHandle } = useSchedule();
  const { resources = [], resourceGrouping, resourcesPerPage: rppConfig, view } = config;

  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(panelConfig.defaultCollapsed ?? false);
  const [width, setWidth] = useState(panelConfig.defaultWidth ?? 220);
  const [unmatchedExpanded, setUnmatchedExpanded] = useState(false);

  const columns = panelConfig.columns ?? [];
  const label = panelConfig.label ?? 'Resources';

  const resourcesPerPage =
    typeof rppConfig === 'number' ? rppConfig : (rppConfig?.[view] ?? 2);

  const paginationActive =
    resourceGrouping !== 'byResource' && resources.length > resourcesPerPage;

  // IDs of resources currently shown in the schedule grid
  const visibleResourceIds = useMemo(() => {
    if (!paginationActive) return new Set(resources.map((r) => r.id));
    const start = resourcePage * resourcesPerPage;
    const end = start + resourcesPerPage;
    return new Set(resources.slice(start, end).map((r) => r.id));
  }, [paginationActive, resources, resourcePage, resourcesPerPage]);

  // Filter resources by search query (name + any custom property value)
  const filteredResources = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return resources;
    return resources.filter((r) => {
      if (r.name.toLowerCase().includes(q)) return true;
      if (!r.customProperties) return false;
      return Object.values(r.customProperties).some(
        (v) => String(v).toLowerCase().includes(q),
      );
    });
  }, [resources, search]);

  // Click a resource row → jump to the page that contains that resource
  const handleResourceClick = useCallback((resourceId: string) => {
    if (!paginationActive) return;
    const idx = resources.findIndex((r) => r.id === resourceId);
    if (idx < 0) return;
    setResourcePage(Math.floor(idx / resourcesPerPage));
  }, [paginationActive, resources, resourcesPerPage, setResourcePage]);

  // Provider selected from matching card
  const handleMatchedProviderClick = useCallback((resourceId: string) => {
    matchingHandle.handleProviderSelected(resourceId);
    handleResourceClick(resourceId);
  }, [matchingHandle, handleResourceClick]);

  // Drag-to-resize right edge
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: MouseEvent) => {
      setWidth(Math.min(PANEL_MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, startW + ev.clientX - startX)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    e.preventDefault();
  }, [width]);

  // ── Matching state ────────────────────────────────────────────────────────

  const { matchingState, matches, unmatchedResourceIds, matchSummary } = matchingHandle;
  const isMatchingActive = matchingState === 'MATCHING_ACTIVE';

  // Build matched resource objects (sorted by matchScore desc, already sorted by hook)
  const matchedResourceObjects = useMemo(() => {
    if (!isMatchingActive) return [];
    return matches
      .map((m) => ({ match: m, resource: resources.find((r) => r.id === m.resourceId) }))
      .filter((item): item is { match: typeof item.match; resource: NonNullable<typeof item.resource> } =>
        item.resource !== undefined,
      );
  }, [isMatchingActive, matches, resources]);

  // Build unmatched resource objects (preserving original resource order)
  const unmatchedResourceObjects = useMemo(() => {
    if (!isMatchingActive) return [];
    const unmatchedSet = new Set(unmatchedResourceIds);
    return filteredResources.filter((r) => unmatchedSet.has(r.id));
  }, [isMatchingActive, unmatchedResourceIds, filteredResources]);

  // Derive active criteria labels for the banner from all matched criteria tags
  const activeCriteria = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const { match } of matchedResourceObjects) {
      for (const c of match.matchedCriteria) {
        if (!seen.has(c)) { seen.add(c); result.push(c); }
      }
    }
    return result.slice(0, 6); // cap at 6 to avoid overflow
  }, [matchedResourceObjects]);

  if (collapsed) {
    return <CollapsedStrip label={label} onExpand={() => setCollapsed(false)} />;
  }

  const hasColumns = columns.length > 0;

  return (
    <div
      style={{
        width,
        flexShrink: 0,
        borderRight: '1px solid #e2e8f0',
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 8px 6px 10px',
          borderBottom: '1px solid #e2e8f0',
          flexShrink: 0,
          background: '#f1f5f9',
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', userSelect: 'none', letterSpacing: '0.01em' }}>
          {label}
        </span>
        <button
          onClick={() => setCollapsed(true)}
          title="Collapse panel"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 5px',
            fontSize: 15,
            color: '#94a3b8',
            lineHeight: 1,
            borderRadius: 3,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#475569'; (e.currentTarget as HTMLButtonElement).style.background = '#e2e8f0'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = '#94a3b8'; (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
        >
          ‹
        </button>
      </div>

      {/* ── Match summary banner (shown when matching is active) ── */}
      {isMatchingActive && (
        <MatchSummaryBanner
          matchSummary={matchSummary}
          activeCriteria={activeCriteria}
          onClear={matchingHandle.clearMatching}
        />
      )}

      {/* ── Search ── */}
      <div style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, position: 'relative' }}>
        <SearchIcon />
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '4px 8px 4px 26px',
            fontSize: 12,
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            background: '#fff',
            color: '#1e293b',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = '#3b82f6'; }}
          onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = '#cbd5e1'; }}
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{
              position: 'absolute',
              right: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#94a3b8',
              fontSize: 13,
              padding: '0 2px',
              lineHeight: 1,
            }}
            title="Clear search"
          >
            ✕
          </button>
        )}
      </div>

      {/* ── Resource list ── */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ═══ MATCHING ACTIVE VIEW ═══ */}
        {isMatchingActive ? (
          <>
            {/* Matched providers */}
            {matchedResourceObjects.length === 0 ? (
              <div style={{ padding: '10px 10px', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
                No providers matched the current criteria
              </div>
            ) : (
              matchedResourceObjects.map(({ resource, match }) => (
                <MatchedResourceCard
                  key={resource.id}
                  resource={resource}
                  match={match}
                  isVisible={visibleResourceIds.has(resource.id)}
                  onProviderClick={handleMatchedProviderClick}
                />
              ))
            )}

            {/* Unmatched providers — collapsible */}
            {unmatchedResourceObjects.length > 0 && (
              <>
                <button
                  onClick={() => setUnmatchedExpanded((x) => !x)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: '5px 8px',
                    gap: 5,
                    background: '#f8fafc',
                    border: 'none',
                    borderTop: '1px solid #e2e8f0',
                    borderBottom: unmatchedExpanded ? 'none' : '1px solid #e2e8f0',
                    cursor: 'pointer',
                    fontSize: 11,
                    color: '#64748b',
                    fontWeight: 600,
                    textAlign: 'left',
                  }}
                >
                  <ChevronIcon expanded={unmatchedExpanded} />
                  {unmatchedResourceObjects.length} other provider{unmatchedResourceObjects.length !== 1 ? 's' : ''}
                </button>

                {unmatchedExpanded && unmatchedResourceObjects.map((resource) => {
                  const isVisible = visibleResourceIds.has(resource.id);
                  return (
                    <button
                      key={resource.id}
                      onClick={() => handleResourceClick(resource.id)}
                      title={paginationActive ? `Jump to ${resource.name}` : resource.name}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        padding: '5px 8px',
                        gap: 6,
                        background: isVisible ? '#eff6ff' : 'transparent',
                        border: 'none',
                        borderBottom: '1px solid #f1f5f9',
                        borderLeft: isVisible ? '3px solid #3b82f6' : '3px solid transparent',
                        cursor: paginationActive ? 'pointer' : 'default',
                        textAlign: 'left',
                        boxSizing: 'border-box',
                        opacity: 0.7,
                      }}
                    >
                      <span
                        style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: resource.color ?? '#64748b',
                          flexShrink: 0, display: 'inline-block',
                        }}
                      />
                      <span
                        style={{
                          flex: 1, fontSize: 12,
                          color: isVisible ? '#1d4ed8' : '#374151',
                          fontWeight: isVisible ? 600 : 400,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                        }}
                      >
                        {resource.name}
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </>
        ) : (
          /* ═══ STANDARD VIEW ═══ */
          <>
            {/* Column headers */}
            {hasColumns && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '3px 8px 3px 10px',
                  borderBottom: '1px solid #e2e8f0',
                  flexShrink: 0,
                  gap: 4,
                  background: '#f8fafc',
                }}
              >
                <span
                  style={{
                    flex: 1, fontSize: 10, fontWeight: 700, color: '#94a3b8',
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  Name
                </span>
                {columns.map((col) => (
                  <span
                    key={col.key}
                    style={{
                      width: col.width ?? 72, flexShrink: 0,
                      fontSize: 10, fontWeight: 700, color: '#94a3b8',
                      textTransform: 'uppercase', letterSpacing: '0.06em',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {col.label}
                  </span>
                ))}
              </div>
            )}

            {filteredResources.length === 0 ? (
              <div style={{ padding: '16px 10px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                No resources found
              </div>
            ) : (
              filteredResources.map((resource) => {
                const isVisible = visibleResourceIds.has(resource.id);
                return (
                  <button
                    key={resource.id}
                    onClick={() => handleResourceClick(resource.id)}
                    title={paginationActive ? `Jump to ${resource.name}` : resource.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      padding: '5px 8px 5px 8px',
                      gap: 6,
                      background: isVisible ? '#eff6ff' : 'transparent',
                      border: 'none',
                      borderBottom: '1px solid #f1f5f9',
                      borderLeft: isVisible ? '3px solid #3b82f6' : '3px solid transparent',
                      cursor: paginationActive ? 'pointer' : 'default',
                      textAlign: 'left',
                      boxSizing: 'border-box',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isVisible && paginationActive)
                        (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      if (!isVisible)
                        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <span
                      style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: resource.color ?? '#64748b',
                        flexShrink: 0, display: 'inline-block',
                      }}
                    />
                    <span
                      style={{
                        flex: 1, fontSize: 12,
                        color: isVisible ? '#1d4ed8' : '#374151',
                        fontWeight: isVisible ? 600 : 400,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
                      }}
                    >
                      {resource.name}
                    </span>
                    {columns.map((col) => (
                      <span
                        key={col.key}
                        style={{
                          width: col.width ?? 72, flexShrink: 0,
                          fontSize: 11, color: isVisible ? '#3b82f6' : '#64748b',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {resource.customProperties?.[col.key] != null
                          ? String(resource.customProperties[col.key])
                          : ''}
                      </span>
                    ))}
                  </button>
                );
              })
            )}
          </>
        )}
      </div>

      {/* ── Footer ── */}
      <div
        style={{
          padding: '4px 10px',
          borderTop: '1px solid #e2e8f0',
          fontSize: 11,
          color: '#94a3b8',
          flexShrink: 0,
          background: '#f8fafc',
        }}
      >
        {isMatchingActive
          ? `${matchedResourceObjects.length} matched · ${unmatchedResourceObjects.length} other`
          : filteredResources.length === resources.length
            ? `${resources.length} ${resources.length === 1 ? 'resource' : 'resources'}`
            : `${filteredResources.length} of ${resources.length} shown`}
      </div>

      {/* ── Resize handle ── */}
      <div
        onMouseDown={handleResizeStart}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 4,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 10,
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(59,130,246,0.35)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
      />
    </div>
  );
}
